import { useFormContext } from '@sravni/cosago-react-library/lib/hooks';
import { useCallback, useMemo } from 'react';

import { sendSentryClientErrorOnce, useAppDispatch, useAppSelector } from 'shared/lib';

import type { TCreditPurposeFormData, TCreditPurposeFormFields, TProductTypeTabId } from 'entities/creditForm';
import {
  selectCreditPurposeInfoData,
  selectCreditPurposeInfoDefaults,
  setCreditPurposeInfo,
  useDictionaries,
} from 'entities/creditForm';

import { PRODUCT_TYPE_TAB_ITEMS } from '../config';

import { getProductTypeTabById } from './getProductTypeTabById';
import { resolveActiveProductTypeTabId } from './resolveActiveProductTypeTabId';

export const useProductTypeSelection = () => {
  const dispatch = useAppDispatch();
  const { setValue, getValues } = useFormContext<TCreditPurposeFormFields>();
  const creditPurposeData = useAppSelector(selectCreditPurposeInfoData);
  const creditPurposeDefaults = useAppSelector(selectCreditPurposeInfoDefaults);
  const { purposes } = useDictionaries(['purposes']);

  const activeTabId = useMemo(() => resolveActiveProductTypeTabId(creditPurposeData), [creditPurposeData]);

  const onSelectTab = useCallback(
    (tabId: TProductTypeTabId) => {
      const tab = getProductTypeTabById(tabId);
      const purpose = purposes.find(({ value }) => value === tab.purpose) ?? creditPurposeData.purpose;

      if (!purpose) {
        sendSentryClientErrorOnce('useProductTypeSelection', 'Purpose is not available on tab select', {
          tabId,
          tabPurpose: tab.purpose,
        });

        return;
      }

      const updateData: Partial<TCreditPurposeFormData> = {
        purpose,
        selectedProductType: tab.selectedProductType,
      };

      const { amount, period } = getValues();
      updateData.amount = amount;
      updateData.period = period ?? creditPurposeDefaults.period;

      setValue('purpose', purpose, { shouldDirty: true, shouldValidate: true });

      dispatch(
        setCreditPurposeInfo({
          data: updateData,
          isFullFilled: false,
          isShowProductTypeSelection: true,
        }),
      );
    },
    [creditPurposeData.purpose, creditPurposeDefaults.period, dispatch, getValues, purposes, setValue],
  );

  return {
    activeTabId,
    onSelectTab,
    tabs: PRODUCT_TYPE_TAB_ITEMS,
  };
};
